import { useEffect, useRef } from 'react';

export default function Hero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
    }> = [];

    for (let i = 0; i < 50; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 2 + 1
      });
    }

    function animate() {
      if (!ctx || !canvas) return;
      
      ctx.fillStyle = 'rgba(10, 14, 39, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0, 212, 255, 0.3)';
        ctx.fill();
      });

      requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#0A0E27] via-[#1A1F3A] to-[#0A0E27]">
      <canvas ref={canvasRef} className="absolute inset-0 opacity-40" />
      
      {/* Grid Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}
      />

      {/* Floating Shield Icon */}
      <div className="absolute right-1/4 top-1/2 -translate-y-1/2 w-96 h-96 flex items-center justify-center opacity-20 animate-pulse">
        <i className="ri-shield-star-line text-[400px] text-[#00D4FF]"></i>
      </div>

      <div className="relative z-10 text-center px-8 w-full max-w-5xl">
        {/* Badge */}
        <div className="inline-flex items-center justify-center px-6 py-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm mb-12">
          <span className="text-white/90 text-xs font-bold tracking-widest uppercase">
            Enterprise-Grade Security
          </span>
        </div>

        {/* Main Headline */}
        <h1 className="text-7xl font-extrabold text-white leading-tight mb-8" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
          Secure Your Digital<br />World With Confidence
        </h1>

        {/* Subtitle */}
        <p className="text-xl text-[#B8C5D6] font-normal mb-14 max-w-2xl mx-auto">
          Military-grade encryption for absolute privacy and protection
        </p>

        {/* CTA Button */}
        <button className="group inline-flex items-center justify-center gap-3 px-12 h-14 rounded-xl bg-gradient-to-r from-[#00D4FF] to-[#0099CC] text-white font-semibold text-lg transition-all duration-300 hover:shadow-2xl hover:shadow-[#00D4FF]/50 hover:-translate-y-1 whitespace-nowrap cursor-pointer">
          Get Protected Now
          <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
        </button>
      </div>
    </section>
  );
}
